
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import { DatabaseInterface, DatabaseRepositoryInterface, DatabaseUserInterface } from '@/services/Database/interfaces';
import { WPXFormValue } from '@/components/templates/WPXForm';
import { EditUserForm } from '@/services/Database/forms/EditUserForm';
import WebsiteRepositoryInterface, { Website } from "@/services/Website/interfaces";
import DatabaseSelectionDialog from "@/views/pages/database/DatabaseSelectionDialog.vue";
import PageHeader from '@/components/layout/PageHeader.vue';

@Component({
  components: { DatabaseSelectionDialog, PageHeader },
  mixins: [
    Themeable,
  ],
  inject: [
    'databaseRepository',
    'serverRepository',
    'websiteRepository',
  ],
})
export default class EditUser extends Vue {
  private form: WPXFormValue<EditUserForm> = new WPXFormValue(EditUserForm);

  private loading: boolean = true;

  private databaseRepository!: DatabaseRepositoryInterface;
  private serverRepository!: ServerRepositoryInterface;
  private websiteRepository!: WebsiteRepositoryInterface;

  private user!: DatabaseUserInterface;
  private servers: ServerInterface[] = [];
  private databases: DatabaseInterface[] = [];
  private userId: string = undefined;

  private selectionDialog: string = "";

  private loadingPassword = false;
  private showPassword = false;


  get selectedDatabaseId(): number | undefined {
    if (this.databases.length <= 0) {
      this.form.data.databaseId = undefined;
      return undefined;
    }
    this.form.data.databaseId = this.databases[0].id;
    return this.form.data.databaseId;
  }

  public async created(): Promise<void> {
    await this.initialize();
    this.loading = false;
  }

  private async togglePasswordVisibility(): Promise<void> {
    if (!this.showPassword && !this.form.data.password) {
      try {
        this.loadingPassword = true;
        this.form.data.password = await this.databaseRepository.getUserPlainPassword(this.user.id);
        this.form.data.passwordConf = this.form.data.password;
        this.showPassword = true;
      } catch (e) {
        this.$errorDialog.show(e?.response?.data || e.message || 'Failed to load password');
        return;
      } finally {
        this.loadingPassword = false;
      }
    } else {
      this.showPassword = !this.showPassword;
    }
  }


  private async initialize(): Promise<void> {
    try {
      const databaseUserId = Number(this.$route.params.userId);
      this.user = await this.databaseRepository.getUserById(databaseUserId);
      if (this.user.databases.length > 0) {
        const database = await this.databaseRepository.getDatabaseById(this.user.databases[0].id);
        this.databases = [database];
        const website = await this.websiteRepository.getWebsiteById(database.websiteId);
        this.userId = website.website.userId;
      } else {
        const isAdmin = this.$store.getters.isAdmin;
        this.userId = isAdmin ? undefined : this.$store.getters.id;
      }

      this.servers = await this.serverRepository.getAllServers();
      this.form.data.databaseId = this.user.databases.map((database) => database.id).pop() ?? 0;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  private async showSelectionDialog(): Promise<void> {
    this.selectionDialog = 'database-selection-dialog';
  }

  public async destroyDialog(): Promise<void> {
    this.selectionDialog = '';
  }

  public async onDatabaseSelected(databaseSelectedId?: string): Promise<void> {
    const selection = JSON.parse(databaseSelectedId) as DatabaseInterface;
    this.databases = [selection];
  }

  private async editUser(): Promise<void> {
    try {
      await this.databaseRepository.editUser(
        this.user.id,
        {
          password: this.form.data.password,
          passwordConf: this.form.data.passwordConf,
          databaseId: this.form.data.databaseId,
        },
      );
      this.$successDialog.show('User Edited Successfully');
      await this.$router.push({ name: 'database_users_list' });
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  private getServerName(serverId: string): string {
    const server = this.servers.find((s) => s._id === serverId);
    if (!server) {
      return '';
    }

    return server.hostname;
  }

  private removeDatabaseItems(): void {
    this.databases = [];
  }
}
